import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";

const BLANK = {
  bank_account_holder_name: "",
  bank_account_number: "",
  confirm_bank_account_number: "",
  bank_ifsc_code: "",
  bank_name: "",
  bank_branch: "",
  bank_account_type: "savings",
};

// The org's own Admin sets where online patient payments are paid out.
// Backend: training BE GET/PUT /organizations/bank-details (organization.py).
// Self-signed-up orgs take no online payments until this is saved.
export default function PayoutBankDetailsCard() {
  const [saved, setSaved] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.fetchOwnBankDetails()
      .then((res) => {
        setSaved(res.data);
        setEditing(!res.data.bank_account_number_last4);
      })
      .catch(() => setSaved(null)); // 403 for non-admins: card stays hidden
  }, []);

  if (!saved) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const digits = (v) => v.replace(/\D/g, "");
  const accountMismatch = form.confirm_bank_account_number.trim() !== "" &&
    digits(form.bank_account_number) !== digits(form.confirm_bank_account_number);
  const canSave = consent && digits(form.bank_account_number) !== "" && !accountMismatch &&
    digits(form.bank_account_number) === digits(form.confirm_bank_account_number);

  const startEdit = () => {
    setForm({
      ...BLANK,
      bank_account_holder_name: saved.bank_account_holder_name || "",
      bank_ifsc_code: saved.bank_ifsc_code || "",
      bank_name: saved.bank_name || "",
      bank_branch: saved.bank_branch || "",
      bank_account_type: saved.bank_account_type || "savings",
    });
    setConsent(false);
    setEditing(true);
  };

  const save = async () => {
    if (digits(form.bank_account_number) !== digits(form.confirm_bank_account_number)) {
      toast.error("The account numbers don't match.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.updateOwnBankDetails({ ...form, bank_details_consent: consent });
      setSaved(res.data);
      setEditing(false);
      setForm(BLANK);
      toast.success("Payout bank details saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not save the bank details.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-md border border-gray-300 p-2 text-sm";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
      <h3 className="text-base md:text-lg font-semibold text-gray-800">Payout Bank Details</h3>
      <p className="text-xs md:text-sm text-gray-500 mt-1">
        Online payments from patients are paid out to this account after each completed consultation.
      </p>

      {saved.online_payment_held && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Online payment is switched off for your patients until you add a bank account. Until then they can only book with Pay at Clinic.
        </div>
      )}

      {!editing ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-700">
            <div className="font-medium">{saved.bank_account_holder_name}</div>
            <div>
              Account ending {saved.bank_account_number_last4} · {saved.bank_ifsc_code}
              {saved.bank_name ? ` · ${saved.bank_name}` : ""}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {saved.bank_verification_status === "verified" ? "Verified" : "Not verified yet"}
            </div>
          </div>
          <button type="button" onClick={startEdit} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-white">
            Change
          </button>
        </div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="bd_holder">Account holder name *</label>
            <input id="bd_holder" className={inputCls} value={form.bank_account_holder_name} onChange={set("bank_account_holder_name")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="bd_type">Account type</label>
            <select id="bd_type" className={inputCls} value={form.bank_account_type} onChange={set("bank_account_type")}>
              <option value="savings">Savings</option>
              <option value="current">Current</option>
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="bd_number">Account number *</label>
            <input id="bd_number" className={inputCls} inputMode="numeric" autoComplete="off" value={form.bank_account_number} onChange={set("bank_account_number")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="bd_confirm">Confirm account number *</label>
            <input id="bd_confirm" className={`${inputCls} ${accountMismatch ? "border-red-500 focus:border-red-500" : ""}`} inputMode="numeric" autoComplete="off"
              value={form.confirm_bank_account_number} onChange={set("confirm_bank_account_number")}
              onPaste={(e) => e.preventDefault()} aria-invalid={accountMismatch} aria-describedby="bd_confirm_error" />
            {accountMismatch && <p id="bd_confirm_error" className="mt-1 text-xs text-red-600">Account numbers don't match.</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="bd_ifsc">IFSC code *</label>
            <input id="bd_ifsc" className={`${inputCls} uppercase`} maxLength={11} value={form.bank_ifsc_code} onChange={set("bank_ifsc_code")} placeholder="HDFC0001234" />
          </div>
          <div>
            <label className={labelCls} htmlFor="bd_bank">Bank name</label>
            <input id="bd_bank" className={inputCls} value={form.bank_name} onChange={set("bank_name")} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="bd_branch">Branch</label>
            <input id="bd_branch" className={inputCls} value={form.bank_branch} onChange={set("bank_branch")} />
          </div>
          <label className="sm:col-span-2 flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>I confirm these bank details are correct and belong to my practice, and I agree to receive payouts to this account.</span>
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button type="button" onClick={save} disabled={saving || !canSave}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-60 text-sm font-medium">
              {saving ? "Saving…" : "Save bank details"}
            </button>
            {saved.bank_account_number_last4 && (
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">
                Cancel
              </button>
            )}
          </div>
          {saved.bank_account_number_last4 && (
            <p className="sm:col-span-2 text-xs text-gray-500">
              Changing the account resets its verification, and we email your practice address to confirm the change.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
